import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Map as MapIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage, useT } from "@/lib/LanguageContext";
// Relative on purpose: real modules under Jest, where there is no "@" alias.
import LoadFailure from "../components/ui/LoadFailure";
import usePathwayAdminList from "../pathways/usePathwayAdminList";
import { pathwayErrorMessage } from "../pathways/errors";
import { PATHWAYS_ADMIN as IDS } from "../constants/testIds/pathwaysAdmin";
import { useEntitlements } from "./entitlements/EntitlementsContext";
import { CAP } from "./entitlements/capabilities";
import CapabilityNotice from "./entitlements/CapabilityNotice";
import { adminHref } from "./adminPath";
import ActionMenu from "./pathways/ActionMenu";
import { statusLabel, typeLabel } from "./pathways/labels";
import { STATUS_PILL, TYPE_BADGE } from "./pathways/statusPill";
import { pathwayPaths } from "./pathways/paths";

/**
 * `/admin/pathways` (spec screen 1, contract §9): one card per pathway.
 *
 *   ┌──────────────────────────────────┐
 *   │ [Level] [Active]              ⋮ │   type badge · status pill · Edit / Duplicate / Archive
 *   │ Prenatal Yoga Foundations        │
 *   │ 24 / 30 enrolled                 │   "N enrolled" when the capacity is unlimited
 *   │ ▓▓▓▓▓▓▓░░░░                      │
 *   │ Avg progress 61%                 │   "—" before anyone enrols
 *   └──────────────────────────────────┘
 *
 * A card opens the pathway's tracking dashboard; "+ Create pathway" opens the builder;
 * Duplicate opens the NEW draft in the builder; Archive asks first (there is no
 * un-archive, contract §5.1).
 *
 * PLAN GATE. Authoring (create, duplicate, every edit) needs `learn.courses`; the server
 * refuses it otherwise. The list, the dashboards, Archive and the builder's read-only
 * view stay open, because existing learners must still be served. `null` (the map is not
 * known yet) claims nothing, as in LearnAdmin.
 */
export default function PathwaysAdmin() {
  const t = useT("admin");
  const tc = useT("common");
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const { rows, loading, error, reload, archive, duplicate } = usePathwayAdminList();
  const ent = useEntitlements();
  const coursesState = ent.status(CAP.courses);
  const canAuthor = coursesState !== "not_in_plan" && coursesState !== "coming_soon";

  const onDuplicate = async (row) => {
    try {
      const copy = await duplicate(row.id, lang);
      toast.success(t("path.duplicated"));
      navigate(adminHref(pathwayPaths.edit(copy.id)));
    } catch (e) {
      toast.error(pathwayErrorMessage(e, tc, tc("errors.saveFailed")));
    }
  };

  const onArchive = async (row) => {
    if (!window.confirm(t("path.confirmArchive", { title: row.title }))) return;
    try {
      await archive(row.id);
      toast.success(t("path.archived"));
    } catch (e) {
      toast.error(pathwayErrorMessage(e, tc, tc("errors.saveFailed")));
    }
  };

  return (
    <div data-testid={IDS.list} className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-display text-3xl text-eh-ink flex items-center gap-3">
          <MapIcon className="w-6 h-6 text-sol-gold" aria-hidden="true" /> {t("path.title")}
        </h1>
        <Button
          onClick={() => navigate(adminHref(pathwayPaths.create))}
          disabled={!canAuthor}
          data-testid={IDS.create}
          className="bg-eh-ink text-eh-cream hover:bg-eh-ink/90"
        >
          <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
          {t("path.create")}
        </Button>
      </div>

      <CapabilityNotice
        state={coursesState}
        eta={ent.eta(CAP.courses)}
        body={t("path.planNotice")}
        testId={IDS.planNotice}
      />

      {loading && !rows ? (
        <Loader2 className="w-6 h-6 animate-spin text-eh-muted" aria-label={t("path.loading")} />
      ) : error ? (
        <LoadFailure
          message={tc("errors.loadFailed")}
          onRetry={reload}
          retryLabel={t("path.retry")}
          testId={IDS.listLoadError}
        />
      ) : rows.length === 0 ? (
        <div data-testid={IDS.listEmpty} className="text-center py-20 bg-white rounded-2xl border border-eh-border-soft px-6">
          <MapIcon className="w-10 h-10 mx-auto text-eh-muted/40 mb-3" aria-hidden="true" />
          <p className="text-eh-muted max-w-md mx-auto">{t("path.emptyState")}</p>
        </div>
      ) : (
        <ul data-testid={IDS.listGrid} className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {rows.map((row) => (
            <PathwayCard
              key={row.id}
              row={row}
              canAuthor={canAuthor}
              onEdit={() => navigate(adminHref(pathwayPaths.edit(row.id)))}
              onDuplicate={() => onDuplicate(row)}
              onArchive={() => onArchive(row)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function PathwayCard({ row, canAuthor, onEdit, onDuplicate, onArchive }) {
  const t = useT("admin");
  const enrolled = row.capacity == null
    ? t("path.card.enrolled", { n: row.learners_count })
    : t("path.card.enrolledOf", { n: row.learners_count, capacity: row.capacity });
  const avg = row.avg_progress;
  return (
    <li
      data-testid={IDS.card(row.id)}
      className="relative bg-white rounded-2xl border border-eh-border-soft p-5 flex flex-col gap-3 hover:border-eh-border transition"
    >
      <div className="flex items-center gap-2">
        <span data-testid={IDS.cardType(row.id)} data-type={row.path_type} className={`text-[11px] px-2.5 py-0.5 rounded-full ${TYPE_BADGE}`}>
          {typeLabel(t, row.path_type)}
        </span>
        <span
          data-testid={IDS.cardStatus(row.id)}
          data-status={row.status}
          className={`text-[11px] px-2.5 py-0.5 rounded-full ${STATUS_PILL[row.status] || STATUS_PILL.draft}`}
        >
          {statusLabel(t, row.status)}
        </span>
        {/* Above the stretched link, so the menu stays clickable. */}
        <div className="ml-auto relative z-10">
          <ActionMenu
            label={t("path.menu.label", { title: row.title })}
            testId={IDS.cardMenu(row.id)}
            items={[
              { key: "edit", label: t("path.menu.edit"), onSelect: onEdit, testId: IDS.cardMenuEdit(row.id) },
              { key: "duplicate", label: t("path.menu.duplicate"), onSelect: onDuplicate, disabled: !canAuthor, testId: IDS.cardMenuDuplicate(row.id) },
              { key: "archive", label: t("path.menu.archive"), onSelect: onArchive, disabled: row.status === "archived", danger: true, testId: IDS.cardMenuArchive(row.id) },
            ]}
          />
        </div>
      </div>
      <Link
        to={adminHref(pathwayPaths.dashboard(row.id))}
        data-testid={IDS.cardOpen(row.id)}
        className="font-display text-xl text-eh-ink leading-snug after:absolute after:inset-0 after:rounded-2xl focus:outline-none focus-visible:after:ring-2 focus-visible:after:ring-eh-sage-deep"
      >
        {row.title}
      </Link>
      <p data-testid={IDS.cardEnrolled(row.id)} className="text-sm text-eh-ink">{enrolled}</p>
      <div className="h-1.5 rounded-full bg-eh-cream overflow-hidden" aria-hidden="true">
        <div className="h-full bg-eh-sage-deep rounded-full" style={{ width: `${avg ?? 0}%` }} />
      </div>
      <p data-testid={IDS.cardProgress(row.id)} className="text-sm text-eh-muted">
        {avg == null ? t("path.card.avgNone") : t("path.card.avg", { p: avg })}
      </p>
    </li>
  );
}
